import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, Send, Loader2, Check, Music, Disc3, ArrowLeft, Megaphone, UtensilsCrossed, Star, Gamepad2, ImagePlus, X as XIcon, ShieldCheck } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import GuestOtpModal from '../components/guest/GuestOtpModal';
import { AmbienceCheckSheet } from '../components/guest/AmbienceCheckSheet';
import VotingBallot from '../components/VotingBallot';
import { guestGet, guestSet } from '../lib/guestStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuestRequestPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Tile chooser on VenueLandingPage may pass startMode via React Router state,
  // letting the guest skip straight to the song search OR the shoutout composer.
  const initialMode = location.state?.startMode || null;
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);

  // Guest info — hydrate from localStorage (via guestStorage helper) so
  // guests aren't re-prompted after closing / re-opening the tab within the
  // 6-hour identity window.
  const STORAGE_KEY = `guest_session_${code}`;
  const stored = guestGet(STORAGE_KEY);
  const [guestName, setGuestName] = useState(stored?.name || '');
  const [guestPhone, setGuestPhone] = useState(stored?.phone || '');
  const [infoSubmitted, setInfoSubmitted] = useState(Boolean(stored?.name && stored?.phone));
  // WhatsApp OTP token (issued by /api/otp/verify). When the venue requires
  // OTP we MUST attach this to the song-request submit; otherwise the request
  // is rejected with a 401. Token persisted via guestStorage so back-nav
  // (e.g. from /menu) doesn't re-prompt the guest.
  const [guestToken, setGuestToken] = useState(stored?.token || '');
  const [otpOpen, setOtpOpen] = useState(false);
  const [mode, setMode] = useState(initialMode); // null = choose, 'request', 'shoutout'

  // Shoutout
  const [shoutMessage, setShoutMessage] = useState('');
  const [shoutSending, setShoutSending] = useState(false);
  const [shoutSent, setShoutSent] = useState(false);
  const [shoutPhoto, setShoutPhoto] = useState(null); // resized base64 data URL or null

  // Search
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [submitted, setSubmitted] = useState(new Set());
  const searchTimeout = useRef(null);

  // Ambience check sheet — appears after a guest taps "Add" on a track.
  // Backend `/check-ambience` returns whether the track fits the current
  // music vibe. Guest sees an accept/reject bottom sheet with reason tags
  // before their request is actually submitted.
  const [ambienceOpen, setAmbienceOpen] = useState(false);
  const [ambienceStatus, setAmbienceStatus] = useState('checking'); // checking | allowed | rejected | error
  const [ambienceResult, setAmbienceResult] = useState(null);
  const [ambienceTrack, setAmbienceTrack] = useState(null);

  // Discovery sections — pre-populated category rows shown when the search
  // box is empty so guests can tap a suggestion without having to think of
  // a specific song. Fetched once from `/sessions/{code}/suggestions`
  // (backend caches 24h so this is essentially free).
  const [suggestionSections, setSuggestionSections] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Language cap — when the venue has `allowed_languages` set, we search Apple
  // Music (clean Indian-language catalogue + reliable genre tags) so we can
  // strictly filter to the venue's permitted languages. When NO cap is set,
  // we fall back to the legacy YouTube-Music-only search.
  const allowedLanguages = session?.allowed_languages || [];
  const venueCode = session?.venue_code || '';
  const useAppleSearch = allowedLanguages.length > 0 && !!venueCode;

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const sessionRes = await axios.get(`${API}/requests/sessions/${code}/info`);
        setSession(sessionRes.data);
      } catch (err) {
        console.error('[GuestRequest] session info failed:', err);
        setExpired(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [code]);

  // "Make it yours" bento grid used to be an alternate landing here. Product
  // decision (Jul 2026): keep a SINGLE consistent home for guests — the venue
  // landing page (`/v/:venueCode`) — and never show the grid variant. So when
  // the guest lands on `/request/:code` WITHOUT an explicit `startMode` (i.e.
  // they didn't tap a tile from the venue landing to get here), redirect them
  // back to the venue landing so they always see the same list of tiles.
  useEffect(() => {
    if (loading || expired) return;
    if (!session?.venue_code) return;
    if (initialMode) return;                    // came from a tile → proceed
    if (location.state?.startMode) return;      // guard against stale reads
    navigate(`/v/${session.venue_code}`, { replace: true });
  }, [loading, expired, session, initialMode, location.state, navigate]);

  const handleSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      if (useAppleSearch) {
        const res = await axios.get(`${API}/venue/${venueCode}/search-apple`, {
          params: { q: searchQuery, limit: 20 },
        });
        // Normalise Apple shape → the same shape the existing render code uses.
        const tracks = (res.data?.tracks || []).map((t) => ({
          // Use apple_id as a deterministic local key; the real YouTube id
          // is resolved in handleSubmitRequest via /apple-to-youtube.
          id: `apple:${t.apple_id}`,
          apple_id: t.apple_id,
          name: t.name,
          artists: t.artist_name ? [t.artist_name] : [],
          album: t.album_name || '',
          album_image: t.artwork_url || '',
          duration_ms: t.duration_ms || 0,
          language: t.language || 'unknown',
          genre_names: t.genre_names || [],
          _source: 'apple',
        }));
        setResults(tracks);
      } else {
        const res = await axios.get(`${API}/requests/sessions/${code}/search`, { params: { q: searchQuery, limit: 10 } });
        setResults(res.data.tracks || []);
      }
    } catch (err) {
      console.error('[GuestRequest] search failed:', err);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  }, [code, useAppleSearch, venueCode]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(searchTimeout.current);
    if (val.trim().length >= 2) {
      searchTimeout.current = setTimeout(() => handleSearch(val), 400);
    } else {
      setResults([]);
    }
  };

  // Fetch curated discovery sections once when the session is ready.
  // Backend caches results 24h per (query, limit), so this is cheap.
  useEffect(() => {
    if (!code || !session || expired) return;
    let cancelled = false;
    (async () => {
      setSuggestionsLoading(true);
      try {
        const res = await axios.get(`${API}/requests/sessions/${code}/suggestions`);
        if (!cancelled) setSuggestionSections(res.data?.sections || []);
      } catch (err) {
        console.warn('[GuestRequest] suggestions fetch failed:', err?.message);
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, session, expired]);

  const handleSubmitRequest = async (track) => {
    // Step 1: open the ambience-check bottom sheet and kick off the check.
    // The actual submit only fires when the guest confirms in the sheet
    // (via `_sendRequest` below) — or bypasses via the error-state CTA.
    if (submitted.has(track.id)) return; // already sent
    setAmbienceTrack(track);
    setAmbienceStatus('checking');
    setAmbienceResult(null);
    setAmbienceOpen(true);
    try {
      const res = await axios.post(
        `${API}/requests/sessions/${code}/check-ambience`,
        { track },
        { timeout: 20000 },
      );
      const data = res.data || {};
      setAmbienceResult(data);
      setAmbienceStatus(data.allowed ? 'allowed' : 'rejected');
    } catch (err) {
      console.warn('[GuestRequest] ambience check failed:', err?.message);
      setAmbienceResult(null);
      setAmbienceStatus('error');
    }
  };

  // Actual submit — fires when guest confirms in the ambience sheet.
  const _sendRequest = async () => {
    const track = ambienceTrack;
    if (!track) return;
    setSubmitting(track.id);
    try {
      // Phase 2: playback is 100% Apple Music. Guest search returns Apple
      // catalog songs directly — submit the request as-is with `id` set to
      // the Apple catalog song id. The host's MusicKit player consumes this
      // unchanged.
      const finalTrack = {
        ...track,
        id: track.apple_id || track.id,
      };
      const payload = {
        guest_name: guestName,
        guest_phone: guestPhone,
        track: finalTrack,
      };
      // If the venue requires WhatsApp verification, attach the OTP token.
      // Backend rejects with 401 if token is missing/expired AND requires it.
      if (session?.wa_otp_required && guestToken) {
        payload.guest_token = guestToken;
      }
      await axios.post(`${API}/requests/sessions/${code}/submit`, payload);
      setSubmitted(prev => new Set([...prev, track.id]));
      toast.success(`"${track.name}" requested!`);
      setAmbienceOpen(false);
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.detail || 'Failed to submit request';
      // OTP token expired/missing → re-trigger verification
      if (status === 401 && session?.wa_otp_required) {
        toast.error('Your session expired — please verify again');
        setGuestToken('');
        const cur = guestGet(STORAGE_KEY) || {};
        delete cur.token;
        guestSet(STORAGE_KEY, cur);
        setOtpOpen(true);
        setAmbienceOpen(false);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(null);
    }
  };

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (!guestName.trim() || !guestPhone.trim()) {
      toast.error('Please enter your name and phone');
      return;
    }
    // Persist for downstream guest flows (e.g. games coupon attribution) AND for back-nav resilience
    const name = guestName.trim();
    const phone = guestPhone.trim();
    guestSet('guestName', name);
    guestSet('guestPhone', phone);
    guestSet(STORAGE_KEY, { name, phone, token: guestToken || '' });
    // If venue enforces WhatsApp OTP AND we don't already have a valid token,
    // open the OTP modal BEFORE marking info as submitted. The modal's
    // onVerified() will set the token + advance the flow.
    if (session?.wa_otp_required && !guestToken) {
      setOtpOpen(true);
      return;
    }
    setInfoSubmitted(true);
  };

  const handleOtpVerified = ({ guest_token, phone, name }) => {
    setGuestToken(guest_token);
    // Backend may normalize phone (+91 prefix etc.). Trust whatever it gave us.
    if (phone) setGuestPhone(phone);
    if (name && !guestName) setGuestName(name);
    guestSet(STORAGE_KEY, {
      name: name || guestName,
      phone: phone || guestPhone,
      token: guest_token,
    });
    guestSet('guestName', name || guestName);
    guestSet('guestPhone', phone || guestPhone);
    setOtpOpen(false);
    setInfoSubmitted(true);
  };

  const formatDuration = (ms) => {
    if (!ms) return '';
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center p-6">
        <Toaster position="top-center" />
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <Music className="w-8 h-8 text-gray-500" />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-2">Session Expired</h1>
          <p className="text-gray-400">This request session is no longer active. Ask your DJ for a new QR code.</p>
        </div>
      </div>
    );
  }

  // Step 1: Guest info form
  if (!infoSubmitted) {
    const logo = session?.venue_logo_url;
    const resolvedLogo = logo
      ? (logo.startsWith('http') ? logo : `${process.env.REACT_APP_BACKEND_URL}${logo}`)
      : '';
    return (
      <div className="min-h-screen relative overflow-hidden bg-black flex flex-col">
        {otpOpen && (
          <GuestOtpModal
            sessionCode={code}
            defaultName={guestName}
            defaultPhone={guestPhone}
            onVerified={handleOtpVerified}
            onCancel={() => setOtpOpen(false)}
          />
        )}
        {/* Full-bleed nightclub hero with motion blur effect */}
        <motion.div
          aria-hidden
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.5, ease: 'easeOut' }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&w=1200)' }}
        />
        {/* Dark vignette + bottom gradient */}
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black" />
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_transparent_0%,_black_85%)]" />

        {/* Color blobs */}
        <motion.div
          aria-hidden
          className="absolute top-20 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#FF007F]/25 blur-[120px]"
          animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="absolute bottom-10 -right-32 w-[26rem] h-[26rem] rounded-full bg-[#00FFFF]/15 blur-[120px]"
          animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />

        {/* Grain overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
        />

        <Toaster position="top-center" />

        {/* Top label strip */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="relative pt-8 px-6 flex items-center justify-between z-10"
        >
          <span className="text-[10px] uppercase tracking-[0.5em] text-white/50 font-['JetBrains_Mono']">
            MusicBox
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#FF007F] font-['JetBrains_Mono']">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" />
            Live
          </span>
        </motion.div>

        {/* Hero typography block */}
        <div className="relative flex-1 flex flex-col justify-end px-6 pb-6 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6"
          >
            {resolvedLogo && (
              <motion.img
                src={resolvedLogo}
                alt={session?.host_name || 'Venue'}
                data-testid="guest-venue-logo"
                className="w-16 h-16 rounded-2xl object-cover ring-1 ring-white/20 mb-5"
                animate={{ boxShadow: ['0 0 30px rgba(255,0,127,0.3)', '0 0 80px rgba(255,0,127,0.65)', '0 0 30px rgba(255,0,127,0.3)'] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            )}
            <p className="text-[11px] uppercase tracking-[0.4em] text-white/60 font-['JetBrains_Mono'] mb-3">
              You&apos;re inside →
            </p>
            <h1 className="text-[68px] sm:text-[88px] leading-[0.85] font-black text-white font-['Outfit'] tracking-[-0.045em]">
              {(session?.host_name || 'the night').split(' ').map((w, i) => (
                <motion.span
                  key={`word-${i}-${w}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 + i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mr-3"
                >
                  {w}
                </motion.span>
              ))}
            </h1>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="origin-left h-1.5 w-24 bg-[#FF007F] mt-5 rounded-full"
              style={{ boxShadow: '0 0 24px rgba(255,0,127,0.7)' }}
            />
            <p className="text-white/70 text-lg mt-5 font-['IBM_Plex_Sans'] font-light max-w-xs">
              Tap in. Take the room.
            </p>
          </motion.div>

          {/* Glass form — angled accent strip */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -top-2 left-6 right-6 h-1 bg-gradient-to-r from-[#FF007F] via-[#9D4CDD] to-[#00FFFF] rounded-full opacity-80 blur-[2px]" />
            <form
              onSubmit={handleInfoSubmit}
              className="bg-white/[0.04] backdrop-blur-2xl border border-white/12 rounded-[28px] p-6 space-y-4 shadow-[0_30px_80px_-20px_rgba(255,0,127,0.4)]"
            >
              <div className="grid grid-cols-1 gap-3">
                <div className="relative">
                  <Label className="absolute -top-2 left-3 px-2 bg-black text-[9px] uppercase tracking-[0.35em] text-white/60 font-['JetBrains_Mono'] z-10">
                    Name
                  </Label>
                  <Input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="What should we call you?"
                    className="bg-white/[0.03] border-white/15 text-white h-14 rounded-2xl text-base px-4 focus:border-[#FF007F] focus:ring-2 focus:ring-[#FF007F]/30"
                    data-testid="guest-name-input"
                  />
                </div>
                <div className="relative">
                  <Label className="absolute -top-2 left-3 px-2 bg-black text-[9px] uppercase tracking-[0.35em] text-white/60 font-['JetBrains_Mono'] z-10">
                    Phone
                  </Label>
                  <Input
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="To find you in the crowd"
                    className="bg-white/[0.03] border-white/15 text-white h-14 rounded-2xl text-base px-4 focus:border-[#FF007F] focus:ring-2 focus:ring-[#FF007F]/30"
                    data-testid="guest-phone-input"
                  />
                </div>
              </div>
              <motion.div whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.01 }}>
                <Button
                  type="submit"
                  className="w-full h-16 bg-gradient-to-r from-[#FF007F] to-[#FF3D00] hover:opacity-95 text-white font-black rounded-2xl text-sm tracking-[0.4em] uppercase font-['Outfit'] shadow-[0_10px_40px_rgba(255,0,127,0.5)]"
                  data-testid="guest-info-submit"
                >
                  Let&apos;s Go →
                </Button>
              </motion.div>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  // Step 2: Choose mode
  if (!mode) {
    const hf = session?.host_features || {};
    const requestsOn = session?.requests_enabled !== false;
    const showSong = hf.song_requests !== false && requestsOn;
    const showShoutout = hf.shoutouts !== false && requestsOn && session?.shoutouts_enabled !== false;
    const showMenu = hf.menus === true;
    const showReviews = hf.reviews !== false && session?.reviews_enabled !== false;
    const showGames = hf.games === true && session?.games_enabled !== false;

    // Each tile gets representative imagery + colour identity
    const tiles = [
      showSong && {
        key: 'song', testid: 'choose-request-song',
        onClick: () => setMode('request'),
        title: 'Request a Song', tag: 'Play it loud',
        Icon: Music, accent: '#FF007F',
        img: 'https://images.pexels.com/photos/164745/pexels-photo-164745.jpeg?auto=compress&w=800',
        span: 'col-span-2', height: 'h-52',
      },
      showShoutout && {
        key: 'shoutout', testid: 'choose-shoutout',
        onClick: () => setMode('shoutout'),
        title: 'Shoutout', tag: 'Hype the room',
        Icon: Megaphone, accent: '#39FF14',
        img: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&w=600',
        span: 'col-span-1', height: 'h-44',
      },
      showMenu && {
        key: 'menu', testid: 'choose-menu',
        onClick: () => navigate(`/menu/${code}`),
        title: 'Menu', tag: 'Eat. Drink. Repeat.',
        Icon: UtensilsCrossed, accent: '#00FFFF',
        img: 'https://images.pexels.com/photos/1283219/pexels-photo-1283219.jpeg?auto=compress&w=600',
        span: 'col-span-1', height: 'h-44',
      },
      showReviews && {
        key: 'review', testid: 'choose-review',
        onClick: () => navigate(`/review/${code}`),
        title: 'Leave a Review', tag: 'AI does the typing',
        Icon: Star, accent: '#FFD700',
        img: 'https://images.pexels.com/photos/4068314/pexels-photo-4068314.jpeg?auto=compress&w=600',
        span: 'col-span-1', height: 'h-44',
      },
      showGames && {
        key: 'games', testid: 'choose-games',
        onClick: () => navigate(`/play/${code}`),
        title: 'Bar Games', tag: 'Win a coupon',
        Icon: Gamepad2, accent: '#FF8C00',
        img: 'https://images.pexels.com/photos/3945667/pexels-photo-3945667.jpeg?auto=compress&w=600',
        span: 'col-span-1', height: 'h-44',
      },
    ].filter(Boolean);

    return (
      <div className="min-h-screen relative overflow-hidden bg-black">
        {/* Atmospheric backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&w=1200)' }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        <motion.div
          aria-hidden
          className="absolute -top-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#FF007F]/20 blur-[140px]"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          aria-hidden
          className="absolute bottom-0 -right-40 w-[28rem] h-[28rem] rounded-full bg-[#9D4CDD]/15 blur-[140px]"
          animate={{ scale: [1, 1.25, 1] }}
          transition={{ duration: 9, repeat: Infinity, delay: 1.5 }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
        />

        <Toaster position="top-center" />

        <div className="relative w-full max-w-md mx-auto px-5 pb-12 pt-7">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-7"
          >
            <span className="text-[10px] uppercase tracking-[0.45em] text-white/40 font-['JetBrains_Mono']">
              Hey, {guestName.split(' ')[0] || 'guest'}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#FF007F] font-['JetBrains_Mono']">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" /> Live
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8"
          >
            <h1 className="text-[58px] sm:text-7xl leading-[0.85] font-black text-white font-['Outfit'] tracking-[-0.045em]">
              Make it<br />
              <span className="italic font-extralight text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#FF8C00] to-[#FFD700]">
                yours.
              </span>
            </h1>
            <p className="text-white/60 text-base mt-3 font-light font-['IBM_Plex_Sans']">
              Six ways to play tonight. Pick one.
            </p>
          </motion.div>

          {/* Bento grid with imagery */}
          <motion.div
            className="grid grid-cols-2 gap-3"
            initial="hidden"
            animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } }}
          >
            {tiles.map((t) => {
              const Icon = t.Icon;
              return (
                <motion.button
                  key={t.key}
                  onClick={t.onClick}
                  data-testid={t.testid}
                  variants={{
                    hidden: { opacity: 0, y: 30, scale: 0.94 },
                    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
                  }}
                  whileHover={{ scale: 1.025, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  className={`${t.span} ${t.height} relative overflow-hidden rounded-3xl text-left border border-white/10 group`}
                >
                  {/* Hero photo */}
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-cover bg-center scale-110 group-hover:scale-115 transition-transform duration-700"
                    style={{ backgroundImage: `url(${t.img})` }}
                  />
                  {/* Color wash */}
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{ background: `linear-gradient(160deg, ${t.accent}55 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.9) 100%)` }}
                  />
                  {/* Edge glow on hover */}
                  <div
                    aria-hidden
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ boxShadow: `inset 0 0 60px ${t.accent}80, 0 0 40px ${t.accent}40` }}
                  />

                  <div className="relative h-full p-4 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div
                        className="w-12 h-12 rounded-2xl backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: `${t.accent}40` }}
                      >
                        <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                      </div>
                    </div>
                    <div className="bg-gradient-to-t from-black/85 via-black/55 to-transparent -mx-4 -mb-4 px-4 pb-4 pt-7 rounded-b-3xl">
                      <p className="text-[9px] uppercase tracking-[0.28em] font-['JetBrains_Mono'] mb-1 font-bold" style={{ color: t.accent }}>
                        {t.tag}
                      </p>
                      <h3 className="text-white font-['Outfit'] font-black text-xl leading-[1.05] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]">
                        {t.title}
                      </h3>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            onClick={() => setInfoSubmitted(false)}
            className="mt-8 text-[10px] uppercase tracking-[0.4em] text-white/30 hover:text-white/70 font-['JetBrains_Mono'] w-full text-center transition-colors"
          >
            ← Back
          </motion.button>
        </div>
      </div>
    );
  }

  // Shoutout flow
  if (mode === 'shoutout') {
    const handleSendShoutout = async (e) => {
      e.preventDefault();
      if (!shoutMessage.trim()) { toast.error('Type a message first'); return; }
      setShoutSending(true);
      try {
        await axios.post(`${API}/requests/sessions/${code}/shoutout`, {
          guest_name: guestName,
          message: shoutMessage.trim(),
          photo_data_url: shoutPhoto || undefined,
        });
        setShoutSent(true);
        setShoutMessage('');
        setShoutPhoto(null);
        toast.success('Shoutout sent — the DJ will see it shortly');
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to send shoutout');
      } finally {
        setShoutSending(false);
      }
    };

    // Client-side downsize: any image → JPEG, max 800px wide, ~75% quality. Keeps payload <200KB.
    const handlePhotoChange = async (e) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // allow re-selecting the same file
      if (!file) return;
      if (!file.type.startsWith('image/')) { toast.error('Please pick an image file'); return; }
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = reject;
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onerror = reject;
          im.onload = () => resolve(im);
          im.src = dataUrl;
        });
        const maxW = 800;
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const out = canvas.toDataURL('image/jpeg', 0.75);
        if (out.length > 250_000) { toast.error('Photo too large even after resize — try another'); return; }
        setShoutPhoto(out);
      } catch (photoErr) {
        console.warn('[Shoutout] photo prepare failed:', photoErr);
        toast.error("Couldn't read that photo, try another");
      }
    };
    return (
      <div className="min-h-screen bg-[#000000] flex flex-col" data-testid="guest-shoutout-page">
        <Toaster position="top-center" />
        <header className="px-4 py-4 border-b border-white/5">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <button
              onClick={() => {
                setShoutSent(false);
                if (session?.venue_code) navigate(`/v/${session.venue_code}`);
                else navigate(-1);
              }}
              className="text-gray-400 hover:text-white"
              data-testid="guest-shoutout-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="text-white font-semibold text-sm font-['Outfit']">Send a Shoutout</p>
              <p className="text-gray-500 text-xs">{session?.host_name}&apos;s session</p>
            </div>
            <div className="w-5" />
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            {shoutSent ? (
              <div className="text-center p-8 glass-surface border border-[#39FF14]/30 rounded-3xl" data-testid="shoutout-success">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#39FF14]/15 flex items-center justify-center">
                  <Check className="w-8 h-8 text-[#39FF14]" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2 font-['Outfit']">Sent for approval</h2>
                <p className="text-gray-400 text-sm mb-5">Your shoutout request has been sent for approval.</p>
                <Button
                  onClick={() => { setShoutSent(false); setShoutMessage(''); setShoutPhoto(null); setMode(null); }}
                  variant="ghost"
                  className="border border-white/10 rounded-full text-xs text-gray-300 font-semibold tracking-wider"
                  data-testid="shoutout-back-to-menu"
                >
                  Back to main menu
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSendShoutout} className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-sm font-semibold mb-2 block">Your message</Label>
                  <textarea
                    value={shoutMessage}
                    onChange={(e) => setShoutMessage(e.target.value.slice(0, 240))}
                    placeholder="Happy birthday to Sarah from table 4!"
                    rows={5}
                    className="w-full glass-surface border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-gray-600 resize-none focus:outline-none focus:border-[#39FF14]/40"
                    data-testid="shoutout-message-input"
                  />
                  <p className="text-xs text-gray-500 mt-1.5 text-right">{shoutMessage.length}/240</p>
                </div>

                {/* Optional photo upload */}
                <div>
                  <Label className="text-gray-300 text-sm font-semibold mb-2 block">
                    Add a photo (optional)
                  </Label>
                  {shoutPhoto ? (
                    <div className="relative rounded-2xl overflow-hidden border border-white/10" data-testid="shoutout-photo-preview">
                      <img src={shoutPhoto} alt="shoutout" className="w-full h-44 object-cover" />
                      <button
                        type="button"
                        onClick={() => setShoutPhoto(null)}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black flex items-center justify-center"
                        data-testid="shoutout-photo-remove"
                      >
                        <XIcon className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <label
                      className="flex items-center justify-center gap-2 w-full h-12 rounded-2xl border border-dashed border-white/15 text-gray-400 hover:text-white hover:border-white/30 cursor-pointer transition-colors"
                      data-testid="shoutout-photo-pick"
                    >
                      <ImagePlus className="w-4 h-4" />
                      <span className="text-sm font-semibold tracking-wider">PICK A PHOTO</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                        data-testid="shoutout-photo-input"
                      />
                    </label>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={shoutSending || !shoutMessage.trim()}
                  className="w-full h-12 bg-[#39FF14] hover:bg-[#2dd60f] text-black font-bold tracking-wider rounded-xl disabled:opacity-40"
                  data-testid="shoutout-submit"
                >
                  {shoutSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
                  SEND SHOUTOUT
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Song request search
  return (
    <div className="min-h-screen bg-[#000000] flex flex-col" data-testid="guest-request-page">
      <Toaster position="top-center" />

      <header className="px-4 py-4 border-b border-white/5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <button
            onClick={() => {
              if (session?.venue_code) navigate(`/v/${session.venue_code}`);
              else navigate(-1);
            }}
            className="text-gray-400 hover:text-white"
            data-testid="guest-request-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-white font-semibold text-sm font-['Outfit']">Request a Song</p>
            <p className="text-gray-500 text-xs">{session?.host_name}&apos;s session</p>
          </div>
          <div className="w-5" />
        </div>
      </header>

      <div className="px-4 py-4">
        {/* Vibe Democracy — when the DJ has flipped the session into
            voting mode, show the ballot UI instead of the search box.
            All the existing search + submit paths become inert until the
            DJ turns voting off. */}
        {session?.voting_mode ? (
          <VotingBallot code={code} session={session} />
        ) : (
        <div className="max-w-lg mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={query}
              onChange={handleQueryChange}
              placeholder="Search for a song..."
              className="pl-12 glass-surface border-white/10 text-white h-12 rounded-xl text-base"
              autoFocus
              data-testid="guest-search-input"
            />
            {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
          </div>
          {useAppleSearch && (
            <p
              className="mt-2 text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono']"
              data-testid="guest-language-cap-banner"
            >
              <span className="text-[#FF007F] font-bold">●</span> Showing only{' '}
              <span className="text-white">
                {allowedLanguages.map((l) => l.charAt(0).toUpperCase() + l.slice(1)).join(' · ')}
              </span>
              {' '}songs · powered by Apple Music
            </p>
          )}
        </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="max-w-lg mx-auto space-y-2">
          {results.length === 0 && query.trim().length >= 2 && !searching && (
            <p className="text-gray-500 text-center py-8 text-sm" data-testid="guest-no-results">
              {useAppleSearch
                ? `No matches in ${allowedLanguages.length === 1 ? allowedLanguages[0] : 'allowed languages'} — try another search`
                : 'No results found'}
            </p>
          )}
          {results.length === 0 && query.trim().length < 2 && (
            <>
              {suggestionsLoading && suggestionSections.length === 0 && (
                <div className="flex items-center justify-center py-12" data-testid="guest-suggestions-loading">
                  <Loader2 className="w-6 h-6 text-gray-600 animate-spin" />
                </div>
              )}
              {suggestionSections.length === 0 && !suggestionsLoading && (
                <div className="text-center py-12">
                  <Music className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">Search for a song to request</p>
                </div>
              )}
              {suggestionSections.map((section) => (
                <div key={section.title} className="mb-6" data-testid={`guest-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <h3 className="text-white font-['Outfit'] font-bold text-sm tracking-wide">{section.title}</h3>
                    <span className="text-[9px] uppercase tracking-[0.25em] text-gray-500 font-['JetBrains_Mono']">Tap to request</span>
                  </div>
                  <div className="space-y-2">
                    {section.tracks.map((track) => {
                      const isSubmitted = submitted.has(track.id);
                      const isSubmitting = submitting === track.id;
                      return (
                        <button
                          key={`${section.title}-${track.id}`}
                          type="button"
                          disabled={isSubmitted || isSubmitting}
                          onClick={() => handleSubmitRequest(track)}
                          className="flex w-full items-center gap-3 p-3 rounded-xl glass-surface border border-white/5 hover:border-[#FF007F]/40 hover:bg-white/[0.02] transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`guest-suggestion-${track.id}`}
                        >
                          {track.album_image ? (
                            <img src={track.album_image} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                              <Music className="w-4 h-4 text-gray-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-semibold truncate">{track.name}</p>
                            <p className="text-gray-500 text-xs truncate">{Array.isArray(track.artists) ? track.artists.join(', ') : (track.artists || '')}</p>
                          </div>
                          {isSubmitting ? (
                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />
                          ) : isSubmitted ? (
                            <Check className="w-4 h-4 text-[#39FF14] flex-shrink-0" />
                          ) : (
                            <Send className="w-4 h-4 text-[#FF007F] flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
          {results.map((track) => {
            const isSubmitted = submitted.has(track.id);
            const isSubmitting = submitting === track.id;
            return (
              <div
                key={track.id}
                className="flex items-center gap-3 p-3 rounded-xl glass-surface border border-white/5 hover:border-white/10 transition-colors"
                data-testid={`guest-track-${track.id}`}
              >
                {track.album_image ? (
                  <img src={track.album_image} alt={track.album} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-white font-medium text-sm truncate">{track.name}</p>
                    {track.language && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10 text-white border border-white/15 flex-shrink-0"
                        data-testid={`track-language-${track.id}`}
                      >
                        {track.language}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs truncate">
                    {Array.isArray(track.artists) ? track.artists.join(', ') : track.artists}
                    {track.duration_ms ? ` · ${formatDuration(track.duration_ms)}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleSubmitRequest(track)}
                  disabled={isSubmitted || isSubmitting}
                  className={`h-9 px-4 rounded-full text-xs font-semibold flex-shrink-0 ${
                    isSubmitted
                      ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                      : 'bg-[#FF007F] hover:bg-[#CC0066] text-white'
                  }`}
                  data-testid={`request-track-${track.id}`}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : isSubmitted ? (
                    <><Check className="w-3.5 h-3.5 mr-1" /> Sent</>
                  ) : (
                    <><Send className="w-3.5 h-3.5 mr-1" /> Request</>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      {otpOpen && (
        <GuestOtpModal
          sessionCode={code}
          defaultName={guestName}
          defaultPhone={guestPhone}
          onVerified={handleOtpVerified}
          onCancel={() => setOtpOpen(false)}
        />
      )}
      <AmbienceCheckSheet
        open={ambienceOpen}
        status={ambienceStatus}
        result={ambienceResult}
        track={ambienceTrack}
        venueName={session?.venue_name || session?.host_name || ''}
        onSend={_sendRequest}
        onTryAnother={() => setAmbienceOpen(false)}
        onClose={() => { if (!submitting) setAmbienceOpen(false); }}
        sending={Boolean(submitting)}
      />
    </div>
  );
};

export default GuestRequestPage;
